import React, { useState, useEffect, useRef, forwardRef } from 'react';

interface NotionTypingInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'placeholder'> {
  label?: string;
  error?: string;
  hint?: string;
  icon?: React.ReactNode;
  placeholders: string[];
  typingSpeed?: number;
  deletingSpeed?: number;
  pauseDuration?: number;
}

export const NotionTypingInput = forwardRef<HTMLInputElement, NotionTypingInputProps>(
  ({
    label,
    error,
    hint,
    icon,
    placeholders,
    typingSpeed = 80,
    deletingSpeed = 40,
    pauseDuration = 2000,
    className = '',
    value,
    onFocus,
    onBlur,
    ...props
  }, ref) => {
    const [displayText, setDisplayText] = useState('');
    const [isTyping, setIsTyping] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFocused, setIsFocused] = useState(false);
    const [showCursor, setShowCursor] = useState(true);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Typing animation effect
    useEffect(() => {
      // Don't animate if input has value or is focused
      if (value || isFocused) {
        setDisplayText('');
        return;
      }

      const currentPlaceholder = placeholders[currentIndex];

      if (isTyping) {
        if (displayText.length < currentPlaceholder.length) {
          timeoutRef.current = setTimeout(() => {
            setDisplayText(currentPlaceholder.slice(0, displayText.length + 1));
          }, typingSpeed);
        } else {
          // Finished typing, pause then start deleting
          timeoutRef.current = setTimeout(() => {
            setIsTyping(false);
          }, pauseDuration);
        }
      } else {
        if (displayText.length > 0) {
          timeoutRef.current = setTimeout(() => {
            setDisplayText(displayText.slice(0, -1));
          }, deletingSpeed);
        } else {
          // Finished deleting, move to next placeholder
          setCurrentIndex((prev) => (prev + 1) % placeholders.length);
          setIsTyping(true);
        }
      }

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, [displayText, isTyping, currentIndex, placeholders, typingSpeed, deletingSpeed, pauseDuration, value, isFocused]);

    // Cursor blink effect
    useEffect(() => {
      if (value || isFocused) {
        setShowCursor(false);
        return;
      }

      const cursorInterval = setInterval(() => {
        setShowCursor((prev) => !prev);
      }, 530);

      return () => clearInterval(cursorInterval);
    }, [value, isFocused]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    const showPlaceholder = !value && !isFocused;

    return (
      <div className="space-y-1.5">
        {label && (
          <label className="block text-sm font-medium text-[#37352F]">
            {label}
          </label>
        )}
        <div className="relative">
          {icon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#787774] pointer-events-none z-10">
              {icon}
            </div>
          )}

          {/* Typing placeholder overlay */}
          {showPlaceholder && (
            <div
              className={`absolute inset-0 flex items-center pointer-events-none ${icon ? 'pl-10' : 'pl-3'} pr-3`}
            >
              <span className="text-[#787774] text-sm truncate">
                {displayText}
                <span
                  className={`inline-block w-[2px] h-[1.1em] bg-[#AEAEAB] ml-[1px] align-middle transition-opacity duration-100 ${
                    showCursor ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              </span>
            </div>
          )}

          <input
            ref={ref}
            value={value}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={`
              w-full px-3 py-2.5
              ${icon ? 'pl-10' : ''}
              notion-typing-input
              text-sm
              ${error ? 'border-red-500 !border-red-500' : ''}
              ${className}
            `}
            {...props}
          />
        </div>
        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}
        {hint && !error && (
          <p className="text-sm text-[#787774]">{hint}</p>
        )}
      </div>
    );
  }
);

NotionTypingInput.displayName = 'NotionTypingInput';

export default NotionTypingInput;
